import React, { FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { CDN_ASSETS_LINK_BASE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { CustomerName } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customer'

import { BodyText } from '../../../atoms/bodyText/BodyText'
import { Image } from '../../../atoms/image/Image'
import { Title } from '../../../atoms/title/Title'
import { BodyTextSize, TitleSize } from '../../../styles/typeStyles'
import { ThemeType } from '../../../utils/themes/ThemeProvider'
import { tID } from '../../../utils/utils'

export interface MicrositeCustomerBrandedBannerProps {
  customerName: string
}

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSection,
  flexDirection: theme.breakpoints.isMobileSized ? 'column' : 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['24px'],
  paddingVertical: theme.spacing['24px'],
  paddingHorizontal: theme.spacing['24px'],
}))

const ImageContainer = styled(Image)<{ theme: ThemeType }>(({ theme }) => ({
  width: theme.breakpoints.isMobileSized ? '200px' : '258px',
  height: theme.breakpoints.isMobileSized ? '56px' : '72px',
}))

export const MicrositeCustomerBrandedBanner: FunctionComponent<MicrositeCustomerBrandedBannerProps> = ({
  customerName,
}) => {
  const { isLyraSelect } = useTheme() as ThemeType

  // Temporary DMHC regulatory banner for Lyra Select (CA) — ACCOUNT-4367.
  // Gated by the `showLyraSelectStateOfEmergencyBanner` LD flag in useUpdatedLyraSelectMicrositeConfig.
  // Safe to remove this branch once the CA state of emergency is lifted and the flag is retired.
  if (isLyraSelect) {
    return (
      <Container testID={tID('MicrositeCustomerBrandedBanner')}>
        <BodyText
          size={BodyTextSize.SMALL}
          textAlign='center'
          text={
            <FormattedMessage
              defaultMessage='For Mendocino, Sonoma, Ventura, Santa Barbara, Santa Cruz, and Orange County residents: In light of the California states of emergency from the November/December storms and May chemical incident, <bold>Lyra is still here to help. For support, call (877) 505-7147.</bold>'
              description='DMHC state of emergency notice banner for Lyra Select California members'
              values={{
                bold: (chunks: string) => <BodyText size={BodyTextSize.SMALL} bold text={chunks} />,
              }}
            />
          }
        />
      </Container>
    )
  }

  if (customerName === CustomerName.VCU_HEALTH) {
    return (
      <Container testID={tID('MicrositeCustomerBrandedBanner')}>
        <Title
          text={
            <FormattedMessage
              defaultMessage='Welcome to your VCU Health HelpLink EAP benefit, powered by Lyra'
              description='Welcome message banner for VCU Health customers on the microsite'
            />
          }
          size={TitleSize.XSMALL}
          textAlign='center'
        />
      </Container>
    )
  }

  let customerLogoUrl
  if (customerName === CustomerName.INSIGHT_GLOBAL) {
    customerLogoUrl = `${CDN_ASSETS_LINK_BASE}/insight_global_banner.png`
  }

  if (!customerLogoUrl) {
    return null
  }

  return (
    <Container testID={tID('MicrositeCustomerBrandedBanner')}>
      <Title
        text={
          <FormattedMessage
            defaultMessage='Lyra Health is in partnership with: '
            description='Text for microsite customer branded banner'
          />
        }
        size={TitleSize.XSMALL}
      />
      <ImageContainer source={customerLogoUrl} contentFit='fill' accessibilityIgnoresInvertColors />
    </Container>
  )
}
